import { Suspense, useEffect, useState } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useFormContext } from 'react-hook-form'
import FormTextInput from '@/shared/components/form-inputs/form-text-input'
import FormPhoneInput from '@/shared/components/form-inputs/form-phone-input'
import { Button } from '@/shared/components/ui/button'
import { phoneSchema, textSchema } from '@/shared/lib/schemas/common.schema'
import { RegisterStateCountryAutocomplete } from '@/shared/components/form-inputs/RegisterStateCountryAutocomplete'
import { AUTH } from '@/shared/constants/backend-api'
import { transformToE164 } from '@/shared/lib/utils/phone.utils'
import errorToast from '@/shared/lib/toast/error.toast'
import successToast from '@/shared/lib/toast/success.toast'
import { setAuthCookie } from '@/features/auth/api/auth.api'
import { getPostLoginRedirectRoute } from '@/shared/utils/auth-redirect'
import { verifySession } from '@/features/auth/api/auth.api'
import AuthBgWrapper from '@/features/auth/components/auth-bg-wrapper'
import AuthCardWrapper from '@/features/auth/components/auth-card-wrapper'
import { loginScreenBgImage } from '@/features/auth/constants/image'
import { useSession } from '@/features/auth/context/session.context'

// Base schema for all users
const completeOAuthBaseSchema = z.object({
  fullname: textSchema(),
  phone: phoneSchema,
})

// Extended schema for homeowners (includes address)
// Note: County is not required here - it will be collected on the profile page
// RegisterStateCountryAutocomplete uses 'home.address', 'home.city', 'home.state' field names
const completeOAuthHomeownerSchema = completeOAuthBaseSchema.extend({
  address: textSchema().optional(),
  city: z.string().min(1, 'City is required').optional(),
  state: z.string().min(1, 'State is required').optional(),
  zipCode: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  home: z.object({
    address: textSchema().optional(),
    city: z.string().min(1, 'City is required').optional(),
    state: z.string().min(1, 'State is required').optional(),
    zipCode: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
  }).optional(),
})

// Union type - will validate based on role
const completeOAuthSchema = z.union([
  completeOAuthBaseSchema,
  completeOAuthHomeownerSchema,
])

type CompleteOAuthFormType = z.infer<typeof completeOAuthBaseSchema> | z.infer<typeof completeOAuthHomeownerSchema>

function CompleteOAuthContent() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const { refreshSession } = useSession()
  const sessionToken = searchParams.get('session')
  const stateParam = searchParams.get('state') // Get state from URL if present
  const [isLoading, setIsLoading] = useState(false)
  const [loadingRole, setLoadingRole] = useState<'volunteer' | 'homeowner' | null>(null)
  const [sessionData, setSessionData] = useState<{
    email?: string
    displayName?: string
    firstName?: string
    lastName?: string
    profilePicture?: string
  } | null>(null)
  const [loadingSession, setLoadingSession] = useState(true)
  const [selectedRole, setSelectedRole] = useState<'volunteer' | 'homeowner' | null>(null)
  const [locationDetails, setLocationDetails] = useState<any | null>(null)

  const formMethods = useForm({
    resolver: zodResolver(completeOAuthBaseSchema), // Base schema, will validate dynamically
    mode: 'onTouched', // Only validate after user interacts with fields
    reValidateMode: 'onChange', // Re-validate on change after initial validation
    defaultValues: {
      fullname: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      latitude: undefined,
      longitude: undefined,
      home: {
        address: '',
        city: '',
        state: '',
        zipCode: '',
        latitude: undefined,
        longitude: undefined,
      },
    },
  })

  const {
    control,
    handleSubmit: formHandleSubmit,
    setValue,
    formState: { errors },
    trigger,
    watch,
    getValues,
  } = formMethods
  
  // Watch form values to determine if buttons should be disabled
  // Note: RegisterStateCountryAutocomplete uses 'home.address', 'home.city', 'home.state' field names
  const formValues = watch() as any
  const fullname = formValues.fullname
  const phone = formValues.phone
  
  // Get current form values including nested home object
  const currentValues = getValues() as any
  const homeData = currentValues.home || {}
  
  // Check both direct field names and 'home.' prefixed names (from RegisterStateCountryAutocomplete)
  const address = formValues.address || homeData.address || currentValues.address
  const city = formValues.city || homeData.city || currentValues.city
  const state = formValues.state || homeData.state || currentValues.state
  
  // Check if all required fields are filled
  // For homeowners, require address, city, state (county moved to profile page)
  // When no role is selected, require at least fullname and phone (address validation happens on submit)
  const isFormValid = selectedRole === 'homeowner'
    ? !!(fullname?.trim() && phone?.trim() && address?.trim() && city?.trim() && state?.trim())
    : !!(fullname?.trim() && phone?.trim()) // For volunteers or when no role selected, just need name and phone

  // Parse state parameter and URL params to restore role
  useEffect(() => {
    // First check URL params (when coming back from /role page)
    const urlRole = searchParams.get('role')
    
    // If role exists in URL but is invalid, redirect to /role page
    if (urlRole && urlRole !== 'volunteer' && urlRole !== 'homeowner') {
      console.warn('[CompleteOAuth] Invalid role in URL:', urlRole, '- redirecting to /role')
      // Preserve OAuth session token if present
      const sessionToken = searchParams.get('session')
      if (sessionToken) {
        sessionStorage.setItem('oauthPendingSessionToken', sessionToken)
        router.push(`/role?oauthSession=${sessionToken}`)
      } else {
        router.push('/role')
      }
      return
    }
    
    if (urlRole === 'volunteer' || urlRole === 'homeowner') {
      sessionStorage.setItem('unifiedAuthRole', urlRole)
      setSelectedRole(urlRole as 'volunteer' | 'homeowner')
      console.log('[CompleteOAuth] Set role from URL param:', urlRole)
      return
    }

    if (stateParam) {
      try {
        // State format: "login|region=2&role=homeowner&team=BlueTeam" or "signup|region=2&role=homeowner"
        const parts = stateParam.split('|')
        if (parts.length > 1) {
          const params = parts[1]
          const urlParams = new URLSearchParams(params)
          
          const region = urlParams.get('region')
          const role = urlParams.get('role')
          const team = urlParams.get('team')
          
          // If role exists in state but is invalid, redirect to /role page
          if (role && role !== 'volunteer' && role !== 'homeowner') {
            console.warn('[CompleteOAuth] Invalid role in state parameter:', role, '- redirecting to /role')
            // Preserve OAuth session token if present
            const sessionToken = searchParams.get('session')
            if (sessionToken) {
              sessionStorage.setItem('oauthPendingSessionToken', sessionToken)
              // Preserve state parameter (without the invalid role) for /role page to restore region/team
              const newStateParams = new URLSearchParams(params)
              newStateParams.delete('role') // Remove invalid role
              const newState = parts[0] + '|' + newStateParams.toString()
              router.push(`/role?oauthSession=${sessionToken}${newState ? `&state=${encodeURIComponent(newState)}` : ''}`)
            } else {
              router.push('/role')
            }
            return
          }
          
          // Store in sessionStorage for use during registration
          if (region) sessionStorage.setItem('unifiedAuthRegion', region)
          if (role && (role === 'volunteer' || role === 'homeowner')) {
            sessionStorage.setItem('unifiedAuthRole', role)
            setSelectedRole(role as 'volunteer' | 'homeowner') // Set the selected role to show only that button
          }
          if (team) {
            sessionStorage.setItem('unifiedAuthTeam', team)
            console.log('[CompleteOAuth] Stored team in sessionStorage:', team)
          }
          
          console.log('[CompleteOAuth] Restored URL params from state:', { region, role, team })
        }
      } catch (error) {
        console.error('[CompleteOAuth] Failed to parse state parameter:', error)
      }
    } else {
      // If no state param, check sessionStorage for role
      const storedRole = sessionStorage.getItem('unifiedAuthRole')
      if (storedRole === 'volunteer' || storedRole === 'homeowner') {
        setSelectedRole(storedRole as 'volunteer' | 'homeowner')
      }
    }
  }, [stateParam, searchParams, router])

  // Load pending session data
  useEffect(() => {
    if (!sessionToken) {
      errorToast('Invalid session. Please try signing in again.')
      router.push('/')
      return
    }

    const loadSession = async () => {
      try {
        const url = `${AUTH.google.pendingSession}?sessionToken=${sessionToken}`
        console.log('[CompleteOAuth] Loading session from:', url)
        console.log('[CompleteOAuth] Backend URL:', import.meta.env.VITE_BASE_BACKEND_URL || '/api')
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        console.log('[CompleteOAuth] Response status:', response.status, response.statusText)

        if (!response.ok) {
          const errorText = await response.text()
          let errorMessage = errorText
          try {
            const errorJson = JSON.parse(errorText)
            errorMessage = errorJson.message || errorText
          } catch (e) {
            // If parsing fails, keep original text
          }
          
          console.error('[CompleteOAuth] Failed to load session:', {
            status: response.status,
            statusText: response.statusText,
            error: errorMessage,
            url,
            backendUrl: import.meta.env.VITE_BASE_BACKEND_URL || '/api',
          })
          
          // Provide more specific error messages
          if (response.status === 404) {
            errorToast('Session expired or not found. Please try signing in again.')
          } else if (response.status === 0 || response.status >= 500) {
            errorToast('Unable to connect to server. Please check your connection and try again.')
          } else {
            errorToast(errorMessage || 'Session expired or invalid. Please try signing in again.')
          }
          router.push('/')
          return
        }

        const data = await response.json()
        console.log('[CompleteOAuth] Session data received:', { success: data.success, hasData: !!data.data })

        if (!data.success) {
          throw new Error(data.message || 'Session not found')
        }

        setSessionData(data.data)
        
        // Pre-fill form with available data
        if (data.data.displayName) {
          setValue('fullname', data.data.displayName)
        } else if (data.data.firstName || data.data.lastName) {
          setValue('fullname', `${data.data.firstName || ''} ${data.data.lastName || ''}`.trim())
        }
      } catch (error) {
        console.error('[CompleteOAuth] Failed to load session:', {
          error: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
          sessionToken: sessionToken?.substring(0, 10) + '...', // Log partial token for debugging
          backendUrl: import.meta.env.VITE_BASE_BACKEND_URL || '/api',
        })
        
        // Check if it's a network error
        if (error instanceof TypeError && error.message.includes('fetch')) {
          errorToast('Network error: Unable to connect to server. Please check your connection and try again.')
        } else {
          errorToast('Session expired or invalid. Please try signing in again.')
        }
        router.push('/')
      } finally {
        setLoadingSession(false)
      }
    }

    loadSession()
  }, [sessionToken, router, setValue])

  // Clear errors when role changes (but don't trigger validation)
  // This prevents validation errors from showing on initial page load
  useEffect(() => {
    if (selectedRole) {
      formMethods.clearErrors()
      // Don't call trigger() - let onTouched mode handle validation
      // This prevents validation errors from showing on initial page load
    }
  }, [selectedRole, formMethods])

  const submitRegistration = async (data: any, userType: 'volunteer' | 'homeowner') => {
    if (!sessionToken) {
      errorToast('Invalid session. Please try signing in again.')
      return
    }

    setIsLoading(true)
    setLoadingRole(userType) // Track which button was clicked

    try {
      // Get all form values directly (including nested home object)
      // This ensures we get all fields even if they weren't in the submit data
      const allFormValues = getValues() as any
      
      // Merge submit data with all form values to ensure we have everything
      const mergedData = {
        ...allFormValues,
        ...data,
        // Ensure home object is merged properly
        home: {
          ...(allFormValues.home || {}),
          ...(data.home || {}),
        },
      }
      
      // Transform phone to E.164 format
      const phoneE164 = transformToE164(mergedData.phone)

      // Get region from sessionStorage (passed from URL)
      const region = sessionStorage.getItem('unifiedAuthRegion') || sessionStorage.getItem('registrationRegion')

      // Get team from sessionStorage if available (for volunteers)
      const team = userType === 'volunteer' 
        ? (sessionStorage.getItem('unifiedAuthTeam') || sessionStorage.getItem('registrationTeam'))
        : undefined

      // Initialize address variables for homeowners
      let addressValue: string | undefined
      let cityValue: string | undefined
      let stateValue: string | undefined

      // For homeowners, validate address fields (county is optional, will be collected on profile page)
      if (userType === 'homeowner') {
        // Check both direct field names and 'home.' prefixed names (from RegisterStateCountryAutocomplete)
        addressValue = mergedData.address || mergedData.home?.address
        cityValue = mergedData.city || mergedData.home?.city
        stateValue = mergedData.state || mergedData.home?.state
        
        console.log('[CompleteOAuth] Initial address check:', {
          addressValue,
          cityValue,
          stateValue,
          hasDataAddress: !!mergedData.address,
          hasDataHome: !!mergedData.home,
          hasHomeAddress: !!mergedData.home?.address,
          hasHomeCity: !!mergedData.home?.city,
          hasHomeState: !!mergedData.home?.state,
          dataKeys: Object.keys(mergedData),
          homeKeys: mergedData.home ? Object.keys(mergedData.home) : [],
          allFormValuesKeys: Object.keys(allFormValues),
          allFormValuesHome: allFormValues.home,
        })
        
        // If address is provided but city/state are missing, try to parse from address string
        // This handles cases where user types full address like "91 Eagle Ct, Crossville, TN 38558, USA"
        if (addressValue && (!cityValue || !stateValue)) {
          const addressParts = addressValue.split(',').map((part: string) => part.trim())
          
          console.log('[CompleteOAuth] Parsing address:', {
            addressValue,
            addressParts,
            addressPartsLength: addressParts.length,
            currentCity: cityValue,
            currentState: stateValue,
          })
          
          // Try to extract city and state from address string
          // Format is typically: "Street, City, State ZIP, Country"
          if (addressParts.length >= 3) {
            // City is usually the second part
            if (!cityValue && addressParts[1]) {
              cityValue = addressParts[1]
              console.log('[CompleteOAuth] Extracted city:', cityValue)
            }
            
            // State is usually the third part (might include ZIP)
            if (!stateValue && addressParts[2]) {
              // Extract state abbreviation (2 letters) or state name
              const statePart = addressParts[2]
              // Remove ZIP code if present (e.g., "TN 38558" -> "TN")
              // Try to match state abbreviation (2 uppercase letters) at the start
              const stateAbbrMatch = statePart.match(/^([A-Z]{2})\s*/)
              if (stateAbbrMatch) {
                stateValue = stateAbbrMatch[1]
                console.log('[CompleteOAuth] Extracted state (abbreviation):', stateValue, 'from:', statePart)
              } else {
                // If no abbreviation, take the first word (might be state name)
                const firstWord = statePart.split(/\s+/)[0]
                if (firstWord && firstWord.length > 0) {
                  stateValue = firstWord
                  console.log('[CompleteOAuth] Extracted state (first word):', stateValue, 'from:', statePart)
                }
              }
            }
          } else {
            console.warn('[CompleteOAuth] Address does not have enough parts to parse:', {
              addressValue,
              addressParts,
              length: addressParts.length,
            })
          }
          
          console.log('[CompleteOAuth] After parsing:', {
            addressValue,
            cityValue,
            stateValue,
            isValid: !!(addressValue && cityValue && stateValue),
          })
        }
        
        console.log('[CompleteOAuth] Address validation:', {
          hasAddress: !!addressValue,
          hasCity: !!cityValue,
          hasState: !!stateValue,
          addressValue,
          cityValue,
          stateValue,
          dataKeys: Object.keys(mergedData),
          hasHome: !!mergedData.home,
          homeKeys: mergedData.home ? Object.keys(mergedData.home) : [],
        })
        
        if (!addressValue || !cityValue || !stateValue) {
          errorToast('Please provide a complete address (address, city, and state). Please select an address from the dropdown or ensure your address includes city and state.')
          setIsLoading(false)
          setLoadingRole(null)
          return
        }
        
        // Update the mergedData object with parsed values if they were extracted
        if (cityValue && !mergedData.city && !mergedData.home?.city) {
          if (!mergedData.home) {
            mergedData.home = {}
          }
          mergedData.home.city = cityValue
        }
        if (stateValue && !mergedData.state && !mergedData.home?.state) {
          if (!mergedData.home) {
            mergedData.home = {}
          }
          mergedData.home.state = stateValue
        }
      }

      // Prepare request body
      const requestBody: any = {
        sessionToken,
        fullname: mergedData.fullname.trim(),
        phone: phoneE164,
        userType: userType,
        ...(team && { team }),
      }

      // Add address data for homeowners
      // Handle both direct field names and 'home.' prefixed names (from RegisterStateCountryAutocomplete)
      if (userType === 'homeowner') {
        // Use the parsed/extracted values from validation above
        requestBody.address = addressValue
        requestBody.city = cityValue
        requestBody.state = stateValue
        
        // Extract zipCode, latitude, and longitude from all possible sources
        // Check mergedData first (includes all form values), then data, then locationDetails
        const zipCodeValue = mergedData.zipCode || mergedData.home?.zipCode || data.zipCode || (data.home as any)?.zipCode || locationDetails?.zipCode
        const latitudeValue = mergedData.latitude !== undefined && mergedData.latitude !== null 
          ? mergedData.latitude 
          : (mergedData.home?.latitude !== undefined && mergedData.home?.latitude !== null
            ? mergedData.home.latitude
            : (data.latitude !== undefined && data.latitude !== null
              ? data.latitude
              : ((data.home as any)?.latitude !== undefined && (data.home as any)?.latitude !== null
                ? (data.home as any).latitude
                : (locationDetails?.latitude !== undefined && locationDetails?.latitude !== null
                  ? locationDetails.latitude
                  : undefined))))
        const longitudeValue = mergedData.longitude !== undefined && mergedData.longitude !== null
          ? mergedData.longitude
          : (mergedData.home?.longitude !== undefined && mergedData.home?.longitude !== null
            ? mergedData.home.longitude
            : (data.longitude !== undefined && data.longitude !== null
              ? data.longitude
              : ((data.home as any)?.longitude !== undefined && (data.home as any)?.longitude !== null
                ? (data.home as any).longitude
                : (locationDetails?.longitude !== undefined && locationDetails?.longitude !== null
                  ? locationDetails.longitude
                  : undefined))))
        
        // County is not required here - will be collected on profile page
        if (zipCodeValue) requestBody.zipCode = zipCodeValue
        if (latitudeValue !== undefined && latitudeValue !== null) requestBody.latitude = Number(latitudeValue)
        if (longitudeValue !== undefined && longitudeValue !== null) requestBody.longitude = Number(longitudeValue)
        if (region) requestBody.region = region
        
        console.log('[CompleteOAuth] Final address data being sent:', {
          address: requestBody.address,
          city: requestBody.city,
          state: requestBody.state,
          zipCode: requestBody.zipCode,
          latitude: requestBody.latitude,
          longitude: requestBody.longitude,
          region: requestBody.region,
        })
      }

      console.log('[CompleteOAuth] Submitting registration:', {
        userType,
        hasAddress: userType === 'homeowner' ? !!data.address : false,
        team,
      })

      const response = await fetch(AUTH.google.complete, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to complete account creation')
      }

      // Set auth cookie
      await setAuthCookie(result.data.accessToken)

      // Refresh session to ensure it's loaded before redirect
      await refreshSession()

      // Clear sessionStorage params after successful registration
      sessionStorage.removeItem('unifiedAuthRegion')
      sessionStorage.removeItem('registrationRegion')
      sessionStorage.removeItem('unifiedAuthRole')
      sessionStorage.removeItem('registrationRole')
      sessionStorage.removeItem('unifiedAuthTeam')
      sessionStorage.removeItem('registrationTeam')
      sessionStorage.removeItem('oauthPendingSessionToken')
      sessionStorage.removeItem('oauthPendingFullname')
      sessionStorage.removeItem('oauthPendingPhone')

      // Redirect based on user type
      if (userType === 'volunteer') {
        // Volunteer goes to profile page
        successToast('Account created successfully!')
        window.location.href = '/profile'
      } else {
        // Homeowner goes to profile page (which will show detailed form if currentStep=1)
        successToast('Account created successfully!')
        window.location.href = '/profile'
      }
    } catch (error) {
      console.error('Failed to complete OAuth:', error)
      errorToast(error instanceof Error ? error.message : 'Failed to complete account creation')
    } finally {
      setIsLoading(false)
      setLoadingRole(null)
    }
  }

  if (loadingSession) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!sessionData) {
    return null // Will redirect in useEffect
  }

  const headerDescription = 'We need a few more details to finish creating your account.'

  return (
    <AuthBgWrapper image={loginScreenBgImage}>
      <AuthCardWrapper
        headerTitle="Create Your Account"
        headerDescription={headerDescription}
      >
        <FormProvider {...formMethods}>
          <form onSubmit={(e) => e.preventDefault()} className="space-y-4 w-full">
            <FormTextInput
              placeholder="Full Name*"
              name="fullname"
              inputGroupProps={{
                label: "Full Name *",
              }}
              control={control}
              autoComplete="name"
            />

            <FormPhoneInput
              placeholder="Phone Number*"
              name="phone"
              inputGroupProps={{
                label: "Phone Number *",
              }}
              control={control}
              autoComplete="tel"
            />

            {/* Show address fields for homeowners (or when no role selected, so user can fill before choosing) */}
            {(selectedRole === 'homeowner' || selectedRole === null) && (
              <div className="space-y-4 pt-4">
                <h3 className="text-lg font-semibold">Home Address</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedRole === null ? 'Required if registering as a homeowner' : 'Required'}
                </p>
                
                <RegisterStateCountryAutocomplete
                  setLocationDetails={setLocationDetails}
                  locationDetails={locationDetails}
                  control={control}
                />
              </div>
            )}

            <div className="space-y-3">
              {/* Show both buttons if no role is selected */}
              {selectedRole === null && (
                <>
                  <Button
                    type="button"
                    className="w-full"
                    disabled={!isFormValid || isLoading}
                    onClick={formHandleSubmit(
                      (data) => submitRegistration(data, 'volunteer'),
                      (errors) => {
                        if (Object.keys(errors).length > 0) {
                          errorToast('Please fill in all required fields')
                        }
                      }
                    )}
                    showLoader={isLoading && loadingRole === 'volunteer'}
                  >
                    {isLoading && loadingRole === 'volunteer' ? 'Continuing...' : 'Register as a Volunteer'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={!isFormValid || isLoading}
                    onClick={formHandleSubmit(
                      (data) => submitRegistration(data, 'homeowner'),
                      (errors) => {
                        if (Object.keys(errors).length > 0) {
                          errorToast('Please fill in all required fields')
                        }
                      }
                    )}
                    showLoader={isLoading && loadingRole === 'homeowner'}
                  >
                    {isLoading && loadingRole === 'homeowner' ? 'Continuing...' : 'Register as a Homeowner'}
                  </Button>
                </>
              )}

              {/* Show volunteer button if role is volunteer and not loading homeowner */}
              {selectedRole === 'volunteer' && loadingRole !== 'homeowner' && (
                <Button
                  type="button"
                  className="w-full"
                  disabled={!isFormValid || isLoading}
                  onClick={formHandleSubmit(
                    (data) => submitRegistration(data, 'volunteer'),
                    (errors) => {
                      if (Object.keys(errors).length > 0) {
                        errorToast('Please fill in all required fields')
                      }
                    }
                  )}
                  showLoader={isLoading && loadingRole === 'volunteer'}
                >
                  {isLoading && loadingRole === 'volunteer' ? 'Continuing...' : 'Register as a Volunteer'}
                </Button>
              )}

              {/* Show homeowner button if role is homeowner and not loading volunteer */}
              {selectedRole === 'homeowner' && loadingRole !== 'volunteer' && (
                <Button
                  type="button"
                  className="w-full"
                  disabled={!isFormValid || isLoading}
                  onClick={formHandleSubmit(
                    (data) => submitRegistration(data, 'homeowner'),
                    (errors) => {
                      if (Object.keys(errors).length > 0) {
                        errorToast('Please fill in all required fields')
                      }
                    }
                  )}
                  showLoader={isLoading && loadingRole === 'homeowner'}
                >
                  {isLoading && loadingRole === 'homeowner' ? 'Continuing...' : 'Register as a Homeowner'}
                </Button>
              )}
            </div>

            <p className="text-xs text-center text-muted-foreground pt-2">
              By continuing, you agree to our Terms & Conditions
            </p>
          </form>
        </FormProvider>
      </AuthCardWrapper>
    </AuthBgWrapper>
  )
}

function CompleteOAuthPage() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      }
    >
      <CompleteOAuthContent />
    </Suspense>
  )
}

export default CompleteOAuthPage

